import { useState, useMemo, useEffect } from 'react';
import { Search, AlertTriangle, Check, FileText, Edit2, Clock, Trash2 } from 'lucide-react';
import { SlidePanel } from '../Common/SlidePanel';
import { anomaliesApi } from '../../lib/api';
import type { PhotoAnomaly } from '../../types';

type ElementType = 'SEGMENT' | 'SUPPORT' | 'STATION' | 'BAY';

interface AnomalyCode {
  code: string;
  description: string;
  category: string | null;
  elementTypes: string[];
  allowedLevels: number[];
  isActive: boolean;
}

export interface AnomalyFormData {
  issueCode: string;
  issueDescription: string;
  evaluationLevel: 1 | 2 | 3;
  note: string;
}

interface AnomalyFormProps {
  elementType: ElementType;
  onSubmit: (data: AnomalyFormData) => void;
  onClose: () => void;
  onDelete?: () => void;  // For deleting anomaly in edit mode
  editingAnomaly?: PhotoAnomaly | null;  // If provided, form is in edit mode
}

// Get allowed levels for an anomaly code, default to [1,2,3] if not specified
function getAllowedLevels(anomalyCode: AnomalyCode): number[] {
  return anomalyCode.allowedLevels && anomalyCode.allowedLevels.length > 0 
    ? anomalyCode.allowedLevels 
    : [1, 2, 3];
}

// Severity level labels
const LEVEL_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'Low', color: 'bg-success text-white' },
  2: { label: 'Medium', color: 'bg-warning text-white' },
  3: { label: 'High', color: 'bg-danger text-white' },
};

// Recently used codes storage key
const RECENT_CODES_KEY = 'gridinspect_recent_anomaly_codes';
const MAX_RECENT_CODES = 8;

// Get recently used codes from localStorage
function getRecentCodes(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_CODES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Save a code to recently used
function saveRecentCode(code: string) {
  try {
    const recent = getRecentCodes();
    // Remove if already exists, then add to front
    const filtered = recent.filter(c => c !== code);
    const updated = [code, ...filtered].slice(0, MAX_RECENT_CODES);
    localStorage.setItem(RECENT_CODES_KEY, JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
}

export function AnomalyForm({ elementType, onSubmit, onClose, onDelete, editingAnomaly }: AnomalyFormProps) {
  const isEditMode = !!editingAnomaly;
  
  // Initialize state from editingAnomaly if in edit mode
  // Support both local format (issueCode, evaluationLevel, note) and server format (code, level, description)
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCode, setSelectedCode] = useState<string | null>(
    editingAnomaly?.issueCode ?? editingAnomaly?.code ?? null
  );
  const [evaluationLevel, setEvaluationLevel] = useState<1 | 2 | 3>(
    (editingAnomaly?.evaluationLevel ?? editingAnomaly?.level ?? 1) as 1 | 2 | 3
  );
  const [note, setNote] = useState(editingAnomaly?.note ?? editingAnomaly?.description ?? '');
  const [anomalyCodes, setAnomalyCodes] = useState<AnomalyCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recentCodes, setRecentCodes] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load recently used codes on mount
  useEffect(() => {
    setRecentCodes(getRecentCodes());
  }, []);

  // Load anomaly codes from server
  useEffect(() => {
    const loadCodes = async () => {
      try {
        setIsLoading(true);
        const response = await anomaliesApi.getCodes({ 
          elementType,
          isActive: true 
        });
        setAnomalyCodes(response.data?.data || []);
      } catch (error) {
        console.error('Failed to load anomaly codes:', error);
        setAnomalyCodes([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadCodes();
  }, [elementType]);

  // Get recently used anomaly codes that are available for current element type
  const recentAnomalyCodes = useMemo(() => {
    return recentCodes
      .map(code => anomalyCodes.find(ac => ac.code === code))
      .filter((ac): ac is AnomalyCode => ac !== undefined);
  }, [recentCodes, anomalyCodes]);

  const filteredAnomalyCodes = useMemo(() => {
    if (!searchQuery) return anomalyCodes;
    const query = searchQuery.toLowerCase();
    return anomalyCodes.filter(
      (ac) =>
        ac.code.toLowerCase().includes(query) ||
        ac.description.toLowerCase().includes(query) ||
        (ac.category && ac.category.toLowerCase().includes(query))
    );
  }, [anomalyCodes, searchQuery]);

  // Group by category if available, otherwise by first letter
  const groupedAnomalyCodes = useMemo(() => {
    const groups: Record<string, typeof anomalyCodes> = {};
    filteredAnomalyCodes.forEach((ac) => {
      const groupKey = ac.category || `Group ${ac.code[0]}`;
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(ac);
    });
    return groups;
  }, [filteredAnomalyCodes]);

  // Get selected anomaly code
  const selectedAnomalyCode = useMemo(() => {
    return anomalyCodes.find((ac) => ac.code === selectedCode) || null;
  }, [anomalyCodes, selectedCode]);

  // Get allowed levels for selected anomaly code
  const allowedLevels = useMemo(() => {
    if (!selectedAnomalyCode) return [1, 2, 3];
    return getAllowedLevels(selectedAnomalyCode);
  }, [selectedAnomalyCode]);

  // Reset evaluation level when code changes if current level is not allowed
  useEffect(() => {
    if (selectedAnomalyCode && !allowedLevels.includes(evaluationLevel)) {
      setEvaluationLevel(allowedLevels[0] as 1 | 2 | 3);
    }
  }, [selectedAnomalyCode, allowedLevels, evaluationLevel]);

  const handleCodeSelect = (code: string) => {
    setSelectedCode(code);
    const anomalyCode = anomalyCodes.find((ac) => ac.code === code);
    if (anomalyCode) {
      const levels = getAllowedLevels(anomalyCode);
      if (!levels.includes(evaluationLevel)) {
        setEvaluationLevel(levels[0] as 1 | 2 | 3);
      }
    }
  };

  const handleSubmit = () => {
    if (!selectedCode || !selectedAnomalyCode) return;
    // Save to recent codes
    saveRecentCode(selectedCode);
    onSubmit({
      issueCode: selectedAnomalyCode.code,
      issueDescription: selectedAnomalyCode.description,
      evaluationLevel,
      note: note.trim(),
    });
  };

  const isValid = selectedCode && allowedLevels.includes(evaluationLevel);

  return (
    <SlidePanel
      isOpen={true}
      onClose={onClose}
      title={isEditMode ? "Edit Anomaly" : "Create Anomaly"}
      subtitle={isEditMode ? `Code: ${editingAnomaly?.issueCode ?? editingAnomaly?.code}` : `Element: ${elementType}`}
      width="lg"
      showOverlay={false}
    >
      <div className="flex flex-col h-full">
        {/* Info Banner */}
        <div className={`${isEditMode ? 'bg-warning/10 dark:bg-warning/20' : 'bg-primary/10 dark:bg-primary/20'} px-6 py-3 flex items-center gap-2`}>
          {isEditMode ? (
            <Edit2 className="w-4 h-4 text-warning flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-primary flex-shrink-0" />
          )}
          <p className="text-sm text-gray-600 dark:text-slate-300">
            {isEditMode ? 'Update anomaly details below' : 'Select anomaly code and severity level'}
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5 bg-white dark:bg-slate-900">
          {/* Selected code display (compact) */}
          {selectedAnomalyCode && (
            <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="font-bold text-primary text-lg">{selectedAnomalyCode.code}</span>
                {selectedAnomalyCode.category && (
                  <span className="text-xs bg-primary/10 dark:bg-primary/20 text-primary px-2 py-0.5 rounded">
                    {selectedAnomalyCode.category}
                  </span>
                )}
                <Check className="w-4 h-4 text-primary ml-auto" />
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">{selectedAnomalyCode.description}</p>
            </div>
          )}

          {/* Severity Level - Horizontal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Severity Level
            </label>
            <div className="flex gap-2">
              {[1, 2, 3].map((level) => {
                const isAllowed = allowedLevels.includes(level);
                const isSelected = evaluationLevel === level;
                const levelInfo = LEVEL_LABELS[level];
                
                return (
                  <button
                    key={level}
                    onClick={() => isAllowed && setEvaluationLevel(level as 1 | 2 | 3)}
                    disabled={!isAllowed}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? `${levelInfo.color} border-transparent`
                        : isAllowed
                          ? 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 bg-white dark:bg-slate-800'
                          : 'border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 text-gray-300 dark:text-slate-600 cursor-not-allowed'
                    }`}
                  >
                    <div className={`text-xl font-bold ${isSelected ? '' : isAllowed ? 'text-gray-700 dark:text-slate-200' : ''}`}>
                      {level}
                    </div>
                    <div className={`text-xs ${isSelected ? 'opacity-90' : isAllowed ? 'text-gray-500 dark:text-slate-400' : ''}`}>
                      {levelInfo.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Note - Compact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add notes about this anomaly..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            />
          </div>

          {/* Code Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Anomaly Code
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search code or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>

          {/* Anomaly codes list */}
          <div className="space-y-3">
            {isLoading ? (
              <div className="text-center text-gray-400 dark:text-slate-500 py-8">
                <p className="text-sm">Loading codes...</p>
              </div>
            ) : Object.keys(groupedAnomalyCodes).length === 0 ? (
              <div className="text-center text-gray-400 dark:text-slate-500 py-8">
                <p className="text-sm">Nothing found</p>
              </div>
            ) : (
              <>
                {/* Recently Used Section */}
                {!searchQuery && recentAnomalyCodes.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-primary uppercase mb-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Recently Used
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {recentAnomalyCodes.map((anomalyCode) => {
                        const isSelected = selectedCode === anomalyCode.code;
                        return (
                          <button
                            key={anomalyCode.code}
                            onClick={() => handleCodeSelect(anomalyCode.code)}
                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                              isSelected
                                ? 'border-primary bg-primary text-white'
                                : 'border-gray-200 dark:border-slate-600 hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 text-danger'
                            }`}
                            title={anomalyCode.description}
                          >
                            {anomalyCode.code}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* All codes by category */}
                {Object.entries(groupedAnomalyCodes).map(([group, items]) => (
                  <div key={group}>
                    <div className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase mb-2">
                      {group}
                    </div>
                    <div className="space-y-1.5">
                      {items.map((anomalyCode) => {
                        const levels = getAllowedLevels(anomalyCode);
                        return (
                          <button
                            key={anomalyCode.code}
                            onClick={() => handleCodeSelect(anomalyCode.code)}
                            className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                              selectedCode === anomalyCode.code
                                ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-1 ring-primary/20'
                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`font-bold text-sm ${
                                selectedCode === anomalyCode.code ? 'text-primary' : 'text-danger'
                              }`}>
                                {anomalyCode.code}
                              </span>
                              <div className="flex gap-0.5 ml-auto">
                                {[1, 2, 3].map((lvl) => (
                                  <span
                                    key={lvl}
                                    className={`w-4 h-4 text-[10px] rounded flex items-center justify-center ${
                                      levels.includes(lvl)
                                        ? lvl === 1 ? 'bg-success/20 text-success' 
                                          : lvl === 2 ? 'bg-warning/20 text-warning'
                                          : 'bg-danger/20 text-danger'
                                        : 'bg-gray-100 dark:bg-slate-700 text-gray-300 dark:text-slate-500'
                                    }`}
                                  >
                                    {lvl}
                                  </span>
                                ))}
                              </div>
                              {selectedCode === anomalyCode.code && (
                                <Check className="w-3 h-3 text-primary" />
                              )}
                            </div>
                            <p className="text-xs text-gray-600 dark:text-slate-400 mt-1 line-clamp-2">
                              {anomalyCode.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Sticky Actions */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          {/* Delete confirmation */}
          {showDeleteConfirm ? (
            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400 mb-2">Delete this anomaly?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onDelete?.();
                    setShowDeleteConfirm(false);
                  }}
                  className="flex-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ) : null}
          
          <div className="flex gap-3">
            {/* Delete button (only in edit mode) */}
            {isEditMode && onDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2.5 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Delete anomaly"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isValid}
              className={`flex-1 px-4 py-2.5 ${isEditMode ? 'bg-warning hover:bg-warning-dark' : 'bg-primary hover:bg-primary-dark'} text-white rounded-lg disabled:bg-gray-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed transition-colors font-medium`}
            >
              {isEditMode ? 'Update' : 'Add Anomaly'}
            </button>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
}
